import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BuildingSummary } from '../model/building.model';
import { Store } from '@ngrx/store';
import { addAllBuildings, findMoreBuildings, removeAllSelectedBuildings } from '../store/buildings.action';
import { DOWNLOAD_BUILDINGS_LIST_SIZE } from '../service/building.service';
import { Observable } from 'rxjs';
import { selectTagNames } from '../store/tag.selector';
import { BuildingListButtonsComponent } from '../building-list-buttons/building-list-buttons.component';
import { ViewportAdapter, NgScrollbar } from 'ngx-scrollbar';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { BuildingListItemComponent } from '../building-list-item/building-list-item.component';
import { RouterLink } from '@angular/router';
import { MatLine } from '@angular/material/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'sibat-building-list',
  template: `
    <sibat-building-list-buttons
      [enableTags]="highlightSelectedBuildings"
      [tags]="tags$ | async"
      [displayAddButton]="displayAddButton"
      [enableLocation]="buildingsCount > 0"
      [enableBatchActions]="!downloadOrAddRemoveDisabled"
      [maxDownloadSize]="maxDownloadSize"
      [selectedBuildings]="buildingsList"
      (addAll)="addAllBuildings()"
      (removeAll)="removeAllSelectedBuildings()"
      (download)="downloadBuildings()"
      (center)="centerOnAll()"
    ></sibat-building-list-buttons>
    @if (this.buildingsCount) {
    <ng-scrollbar [sensorThrottleTime]="1">
      <div
        infinite-scroll
        scrollViewport
        [scrollWindow]="false"
        [infiniteScrollDistance]="scrollDistance"
        [infiniteScrollThrottle]="throttle"
        (scrolled)="onScrollEnd()"
      >
        <div class="list single-action-list">
          @for (building of buildingsList; track trackBuildingId($index, building)) {
          <sibat-building-list-item
            [routerLink]="'/building/' + building.id"
            [selected]="building.selected && !highlightSelectedBuildings"
            [isInSelectedList]="highlightSelectedBuildings"
            [building]="building"
            [color]="building.currentColor"
            [attributesDisplay]="attributesDisplay"
            (centerOnBuilding)="centerOnSpecifiedBuilding($event)"
            class="clickable-item"
          >
          </sibat-building-list-item>
          }
        </div>
      </div>
    </ng-scrollbar>
    } @else {
    <p class="empty-list" matLine>{{ 'building.list.empty' | transloco }}</p>
    }
  `,
  styleUrls: ['building-list.component.scss'],
  imports: [
    BuildingListButtonsComponent,
    ViewportAdapter,
    NgScrollbar,
    InfiniteScrollDirective,
    BuildingListItemComponent,
    RouterLink,
    MatLine,
    TranslocoPipe,
    AsyncPipe,
  ],
})
export class BuildingListComponent implements OnInit, OnChanges {
  @Input() buildingsList: BuildingSummary[];
  @Input() buildingsCount: number;
  @Input() highlightSelectedBuildings: boolean;
  @Input() displayAddButton: boolean;
  @Input() attributesDisplay: Map<string, boolean>;
  @Output() downloadList = new EventEmitter<boolean>();
  @Output() centerOnBuilding = new EventEmitter<number>();
  @Output() centerOnAllBuildings = new EventEmitter<any>();

  selectedBuildingsListEmpty = true;
  downloadOrAddRemoveDisabled: boolean;
  maxDownloadSize = DOWNLOAD_BUILDINGS_LIST_SIZE;

  throttle = 300;
  scrollDistance = 2;

  tags$: Observable<string[]>;

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.tags$ = this.store.select(selectTagNames);
    this.refreshButtons();
  }

  trackBuildingId(index: number, item: BuildingSummary) {
    return item.id;
  }

  ngOnChanges(changes: SimpleChanges) {
    this.selectedBuildingsListEmpty = changes.buildingsCount?.currentValue === 0;
    this.refreshButtons();
  }

  refreshButtons() {
    this.downloadOrAddRemoveDisabled = this.buildingsCount > DOWNLOAD_BUILDINGS_LIST_SIZE || this.buildingsCount === 0;
  }

  onScrollEnd() {
    this.store.dispatch(findMoreBuildings());
  }

  downloadBuildings() {
    this.downloadList.emit(this.highlightSelectedBuildings);
  }

  addAllBuildings() {
    this.store.dispatch(addAllBuildings());
  }

  removeAllSelectedBuildings() {
    this.store.dispatch(removeAllSelectedBuildings());
  }

  centerOnAll() {
    this.centerOnAllBuildings.emit();
  }

  centerOnSpecifiedBuilding(buildingId: number) {
    this.centerOnBuilding.emit(buildingId);
  }
}
